import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { getPostEmailConfirmPath } from '../lib/resumeOnlyMode'
import { supabase } from '../lib/supabase'

import EmailConfirm from './EmailConfirm'

/**
 * Wrapper component that checks if user is already confirmed BEFORE rendering EmailConfirm
 * This prevents page flashing when user is already confirmed
 */
function EmailConfirmWrapper() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [shouldRender, setShouldRender] = useState(false)
  const previewMode = searchParams.get('preview') === 'true'

  useEffect(() => {
    const checkAndRedirect = async () => {
      // Skip check in preview mode - allow rendering immediately
      if (previewMode) {
        setShouldRender(true)
        return
      }

      try {
        // Fast check: get session (cached, should be instant)
        const { data: { session } } = await supabase.auth.getSession()

        if (session?.user) {
          // Check if email is confirmed
          const isEmailConfirmed =
            session.user.email_confirmed_at ||
            session.user.app_metadata?.provider !== 'email'

          if (isEmailConfirmed) {
            // Email already confirmed - check onboarding status and redirect immediately
            const { data: onboardingData } = await supabase
              .from('user_onboarding')
              .select('completed_at')
              .eq('user_id', session.user.id)
              .maybeSingle()

            navigate(
              getPostEmailConfirmPath(!!onboardingData?.completed_at),
              { replace: true },
            )
            // Don't set shouldRender - page won't render
            return
          }
        }

        // User not confirmed or not authenticated - allow EmailConfirm to render
        setShouldRender(true)
      } catch (err) {
        console.error('Error checking confirmation status:', err)
        // On error, allow page to render (let EmailConfirm handle it)
        setShouldRender(true)
      }
    }

    checkAndRedirect()
  }, [navigate, previewMode])

  // Don't render EmailConfirm until check is complete (prevents flashing)
  if (!shouldRender) {
    return null // Absolutely nothing - prevents any flash
  }

  return <EmailConfirm />
}

export default EmailConfirmWrapper
